import React, { useState } from 'react';
import { Eye, EyeOff, Lock, ArrowRight, Check } from '../../icons';
import BrandMark from '../layout/BrandMark';
import Button from '../ui/Button';

const AuthScreen = ({ onGoogleSignIn, authError }) => {
  const [invitationCode, setInvitationCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const validateInvitationCode = async () => {
    if (!invitationCode.trim()) {
      setCodeError('Please enter an invitation code');
      return;
    }

    setIsValidating(true);
    setCodeError('');

    // Simulate validation delay for better UX
    await new Promise(resolve => setTimeout(resolve, 800));

    const validCode = process.env.REACT_APP_INVITATION_CODE;
    
    if (invitationCode.trim() === validCode) {
      setIsCodeValid(true);
      setCodeError('');
    } else {
      setCodeError('Invalid invitation code. Please try again.');
    }
    
    setIsValidating(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      validateInvitationCode();
    }
  };

  return (
    <div className="min-h-screen bg-page flex items-center justify-center p-4 pt-safe pb-safe relative overflow-hidden">
      {/* Soft teal glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-accent/10 blur-3xl" />

      <div className="w-full max-w-sm relative">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <BrandMark size={72} className="mx-auto mb-4 shadow-pop rounded-2xl" />
          <h1 className="text-[28px] font-bold text-primary tracking-tight">BZ-Tracker</h1>
          <p className="text-secondary text-sm mt-1">Property tracking made simple</p>
        </div>

        {/* Main Card */}
        <div className="bg-card border border-line rounded-sheet p-6 shadow-pop">
          {!isCodeValid ? (
            /* Invitation Code Step */
            <div className="space-y-5">
              <div className="text-center">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-accent-soft text-accent items-center justify-center mb-3">
                  <Lock className="w-6 h-6" />
                </span>
                <h2 className="text-lg font-semibold text-primary">Access required</h2>
                <p className="text-secondary text-sm mt-1">Enter your invitation code to continue</p>
              </div>

              <div className="space-y-3">
                <div className="relative">
                  <input
                    type={showCode ? 'text' : 'password'}
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Invitation code"
                    aria-label="Invitation code"
                    autoComplete="one-time-code"
                    className="input pr-12 h-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCode(!showCode)}
                    aria-label={showCode ? 'Hide code' : 'Show code'}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-secondary hover:text-primary"
                  >
                    {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {codeError && (
                  <div role="alert" className="text-danger text-sm text-center bg-danger-soft border border-danger/30 rounded-xl p-3">
                    {codeError}
                  </div>
                )}

                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={validateInvitationCode}
                  disabled={isValidating || !invitationCode.trim()}
                  loading={isValidating}
                >
                  {isValidating ? 'Validating' : 'Continue'}
                  {!isValidating && <ArrowRight className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          ) : (
            /* Google Sign In Step */
            <div className="space-y-5">
              <div className="text-center">
                <span className="inline-flex w-12 h-12 rounded-full bg-accent-soft text-accent items-center justify-center mb-3">
                  <Check className="w-6 h-6" />
                </span>
                <h2 className="text-lg font-semibold text-primary">Welcome!</h2>
                <p className="text-secondary text-sm mt-1">Sign in with your Google account to get started</p>
              </div>

              {authError && (
                <div role="alert" className="text-danger text-sm text-center bg-danger-soft border border-danger/30 rounded-xl p-3">
                  {authError}
                </div>
              )}

              <button
                type="button"
                onClick={onGoogleSignIn}
                className="w-full h-12 bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-900 font-semibold px-4 rounded-xl transition-colors flex items-center justify-center gap-3 shadow"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>
            </div>
          )}
        </div>

        <p className="text-center mt-6 text-muted text-xs">Secure · Private · Professional</p>
      </div>
    </div>
  );
};

export default AuthScreen;
